// app/components/OdometerCorrectionModal.tsx
import { Portal, Modal, Text, Button, TextInput, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { geocodeAddressToPointWKT } from '../api/utils/geocoding';
import { getLocationString, isLocationUnavailableError } from '../utils/locationUtils';

interface PreviousSessionOut {
  id: number;
  odometer: number;
  timestamp: string;
}

interface OdometerCorrectionModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (correctedOdometer: number, observations: string, locationWkt?: string) => Promise<void>;
  previousSessionOut: PreviousSessionOut;
  newOdometer: number;
  isLoading?: boolean;
}

interface CorrectionFormData {
  correctedOdometer: string;
  observations: string;
}

export const OdometerCorrectionModal = ({
  visible,
  onDismiss,
  onConfirm,
  previousSessionOut,
  newOdometer,
  isLoading = false,
}: OdometerCorrectionModalProps) => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const [correctedOdometer, setCorrectedOdometer] = useState('');
  const [observations, setObservations] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [manualLocationRequired, setManualLocationRequired] = useState(false);
  const [manualLocationText, setManualLocationText] = useState('');
  const [manualLocationError, setManualLocationError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleConfirm = async () => {
    const odometerValue = parseInt(correctedOdometer, 10);
    
    if (!correctedOdometer.trim() || isNaN(odometerValue)) {
      setError(t('odometerCorrection.odometerRequired', 'Please enter a valid odometer value.'));
      return;
    }

    if (odometerValue < 0) {
      setError(t('odometerCorrection.odometerInvalid', 'Odometer must be a positive number.'));
      return;
    }

    if (odometerValue >= previousSessionOut.odometer) {
      setError(t('odometerCorrection.odometerTooHigh', 'Corrected odometer must be less than {{previous}} km to allow starting with {{new}} km.', {
        previous: previousSessionOut.odometer,
        new: newOdometer
      }));
      return;
    }

    if (!observations.trim()) {
      setError(t('odometerCorrection.observationsRequired', 'Please provide observations explaining the correction.'));
      return;
    }

    setError(null);
    let locationWkt: string | undefined;

    try {
      locationWkt = await getLocationString();
    } catch (locationError) {
      if (!isLocationUnavailableError(locationError)) {
        setError(t('odometerCorrection.locationError', 'Unable to obtain a valid location. Please try again.'));
        return;
      }

      setManualLocationRequired(true);
      if (!manualLocationText.trim()) {
        setManualLocationError(t('manualLocationPrompt', {
          ns: 'translation',
          defaultValue: 'Alternatively, enter your location below (address, city, or place).',
        }));
        return;
      }

      setIsGeocoding(true);
      setManualLocationError(null);
      try {
        const geo = await geocodeAddressToPointWKT(manualLocationText.trim());
        if (!geo) {
          setManualLocationError(t('locationNotFound', {
            ns: 'translation',
            defaultValue: 'Location not found. Please try another address.',
          }));
          return;
        }
        locationWkt = geo.wkt;
      } catch (geocodingError) {
        console.error('Error geocoding manual correction location:', geocodingError);
        setManualLocationError(t('geocodingError', {
          ns: 'translation',
          defaultValue: 'Could not find that location. Please try again.',
        }));
        return;
      } finally {
        setIsGeocoding(false);
      }
    }

    if (!locationWkt) {
      setError(t('odometerCorrection.locationError', 'Unable to obtain a valid location. Please try again.'));
      return;
    }

    await onConfirm(odometerValue, observations, locationWkt);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[
          {
            backgroundColor: theme.colors.surface,
            padding: 20,
            margin: 20,
            borderRadius: 8,
          },
          Platform.OS === 'web' ? {
            maxWidth: 500,
            width: '90%',
            alignSelf: 'center',
          } : {}
        ]}
      >
        <Text variant="headlineSmall" style={{ marginBottom: 20 }}>
          {t('odometerCorrection.title', 'Correct Previous Session Odometer')}
        </Text>
        
        <Text variant="bodyMedium" style={{ marginBottom: 16 }}>
          {t('odometerCorrection.message', 'The odometer you entered ({{new}} km) is lower than the previous session end ({{previous}} km). Would you like to correct the previous session\'s odometer?', {
            new: newOdometer,
            previous: previousSessionOut.odometer
          })}
        </Text>

        <View style={{ marginBottom: 16, padding: 12, backgroundColor: theme.colors.surfaceVariant, borderRadius: 8 }}>
          <Text variant="bodySmall" style={{ marginBottom: 4, fontWeight: 'bold' }}>
            {t('odometerCorrection.previousSession', 'Previous Session')}:
          </Text>
          <Text variant="bodySmall" style={{ marginBottom: 4 }}>
            {t('odometerCorrection.date', 'Date')}: {formatTimestamp(previousSessionOut.timestamp)}
          </Text>
          <Text variant="bodySmall">
            {t('odometerCorrection.currentOdometer', 'Current odometer')}: {previousSessionOut.odometer} km
          </Text>
        </View>

        <TextInput
          label={t('odometerCorrection.correctedOdometerLabel', 'Corrected odometer (km)')}
          mode="outlined"
          value={correctedOdometer}
          onChangeText={(text) => {
            setCorrectedOdometer(text);
            setError(null);
          }}
          keyboardType="numeric"
          style={{ marginBottom: 12 }}
          disabled={isLoading}
          error={!!error}
        />

        <TextInput
          label={t('odometerCorrection.observationsLabel', 'Observations (required)')}
          mode="outlined"
          value={observations}
          onChangeText={(text) => {
            setObservations(text);
            setError(null);
          }}
          multiline
          numberOfLines={3}
          style={{ marginBottom: 12 }}
          disabled={isLoading || isGeocoding}
        />

        {manualLocationRequired && (
          <View>
            <TextInput
              label={t('odometerCorrection.manualLocationLabel', {
                defaultValue: 'Location (address, city, or place)',
              })}
              mode="outlined"
              value={manualLocationText}
              onChangeText={(text) => {
                setManualLocationText(text);
                setManualLocationError(null);
              }}
              style={{ marginBottom: 12 }}
              disabled={isLoading || isGeocoding}
              testID="odometer-correction-manual-location"
            />
            {manualLocationError && (
              <Text style={{ color: theme.colors.error, marginBottom: 12 }}>
                {manualLocationError}
              </Text>
            )}
          </View>
        )}

        {error && (
          <Text style={{ color: theme.colors.error, marginBottom: 12 }}>
            {error}
          </Text>
        )}

        <Button
          mode="contained"
          onPress={handleConfirm}
          loading={isLoading || isGeocoding}
          disabled={isLoading || isGeocoding}
          style={{ marginBottom: 10 }}
        >
          {t('odometerCorrection.confirm', 'Correct Previous Session')}
        </Button>
        <Button
          mode="text"
          onPress={onDismiss}
          disabled={isLoading}
        >
          {t('cancel', { ns: 'translation' })}
        </Button>
      </Modal>
    </Portal>
  );
};

